import { type ReactNode, type RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { ApiError } from '@/api/client'
import { ModalCloseButton } from '@/components/ModalCloseButton'
import {
  createPoule,
  fetchPouleCreateCountries,
  fetchPouleCreateLeagues,
  fetchPouleCreateTeams,
  fetchPouleCreateTournaments,
  type PouleCreateCountry,
  type PouleCreateLeague,
  type PouleCreatePayload,
  type PouleCreateTeam,
  type PouleCreateTournament,
} from '@/features/poules/api'
import { useTranslation } from '@/i18n'

type Step = 0 | 1 | 2 | 3
type ScopePickerView = 'type' | 'tournaments' | 'countries' | 'leagues' | 'league-detail'
type ScopeType = 'club' | 'competition' | 'tournament'
type AudienceType = 'friends' | 'company'
type ReleaseMode = 'all_open' | 'stage_by_stage'

type ScopeSelection =
  | { type: 'club'; item: PouleCreateTeam }
  | { type: 'competition'; item: PouleCreateLeague }
  | { type: 'tournament'; item: PouleCreateTournament }

type ToastState = {
  message: string
  tone: 'error'
}

const TEAM_SCOPE_TYPE = 'App\\Models\\Team' as const
const LEAGUE_SCOPE_TYPE = 'App\\Models\\League' as const

export function PouleCreatePage({ onClose }: { onClose?: () => void } = {}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [step, setStep] = useState<Step>(0)
  const [scope, setScope] = useState<ScopeSelection | null>(null)
  const [scopePickerView, setScopePickerView] = useState<ScopePickerView>('type')
  const [selectedCountry, setSelectedCountry] = useState<PouleCreateCountry | null>(null)
  const [selectedLeague, setSelectedLeague] = useState<PouleCreateLeague | null>(null)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [audienceType, setAudienceType] = useState<AudienceType>('friends')
  const [visibility, setVisibility] = useState<'public' | 'private'>('public')
  const [requiresApproval, setRequiresApproval] = useState(false)
  const [releaseMode, setReleaseMode] = useState<ReleaseMode>('all_open')
  const [thumbnailImage, setThumbnailImage] = useState<File | undefined>()
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null)
  const [createdPoule, setCreatedPoule] = useState<{ id: number; name: string; audienceType: AudienceType } | null>(null)
  const [isClosing, setIsClosing] = useState(false)
  const [toast, setToast] = useState<ToastState | null>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const finishClose = useCallback(() => {
    if (onClose) {
      onClose()
      return
    }

    navigate('/dashboard/poules')
  }, [navigate, onClose])
  const requestClose = useCallback(() => {
    setIsClosing(true)
  }, [])

  const countriesQuery = useQuery({
    queryKey: ['poule-create-countries'],
    queryFn: fetchPouleCreateCountries,
    enabled: step === 0 && scope === null && scopePickerView === 'countries',
    staleTime: 24 * 60 * 60 * 1000,
  })
  const leaguesQuery = useQuery({
    queryKey: ['poule-create-leagues', selectedCountry?.id],
    queryFn: () => fetchPouleCreateLeagues(selectedCountry!.id),
    enabled: step === 0 && selectedCountry !== null && selectedLeague === null && scopePickerView === 'leagues',
    staleTime: 30 * 60 * 1000,
  })
  const teamsQuery = useQuery({
    queryKey: ['poule-create-teams', selectedLeague?.id],
    queryFn: () => fetchPouleCreateTeams(selectedLeague!.id),
    enabled: step === 0 && selectedLeague !== null && scope === null && scopePickerView === 'league-detail',
    staleTime: 30 * 60 * 1000,
  })
  const tournamentsQuery = useQuery({
    queryKey: ['poule-create-tournaments'],
    queryFn: fetchPouleCreateTournaments,
    enabled: step === 0 && scope === null && scopePickerView === 'tournaments',
    staleTime: 30 * 60 * 1000,
  })

  const createMutation = useMutation({
    mutationFn: (payload: PouleCreatePayload) => createPoule(payload),
    onSuccess: (result) => {
      setCreatedPoule({ id: result.poule.id, name: result.poule.name, audienceType })
      void queryClient.invalidateQueries({ queryKey: ['joined-poules'] })
    },
  })

  useEffect(() => {
    if (!thumbnailImage) {
      setThumbnailPreview(null)
      return
    }

    const url = URL.createObjectURL(thumbnailImage)
    setThumbnailPreview(url)
    return () => URL.revokeObjectURL(url)
  }, [thumbnailImage])

  useEffect(() => {
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') requestClose()
    }

    window.addEventListener('keydown', handleEscape)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', handleEscape)
    }
  }, [requestClose])

  useEffect(() => {
    if (!isClosing) return

    const timeout = window.setTimeout(finishClose, 300)
    return () => window.clearTimeout(timeout)
  }, [finishClose, isClosing])

  useEffect(() => {
    if (!toast) return

    const timeout = window.setTimeout(() => setToast(null), 3200)
    return () => window.clearTimeout(timeout)
  }, [toast])

  useLayoutEffect(() => {
    contentRef.current?.scrollTo({ top: 0, behavior: 'auto' })
  }, [step])

  function resetScope() {
    setScope(null)
    setScopePickerView('type')
    setSelectedCountry(null)
    setSelectedLeague(null)
  }

  function selectCountry(country: PouleCreateCountry) {
    setScopePickerView('leagues')
    setSelectedCountry(country)
    setSelectedLeague(null)
    setScope(null)
  }

  function selectLeague(league: PouleCreateLeague) {
    setScopePickerView('league-detail')
    setSelectedLeague(league)
    setScope(null)
  }

  function selectTournament(tournament: PouleCreateTournament) {
    setScope({ type: 'tournament', item: tournament })
    setSelectedCountry(null)
    setSelectedLeague(null)
    setScopePickerView('type')
  }

  function backToCountries() {
    setScopePickerView('countries')
    setSelectedCountry(null)
    setSelectedLeague(null)
  }

  function backToLeagues() {
    setScopePickerView('leagues')
    setSelectedLeague(null)
  }

  function goNext() {
    if (step === 0) {
      if (!scope) {
        setToast({ message: t('Kies eerst wat voor poule je wilt maken.'), tone: 'error' })
        return
      }
    }

    if (step === 2) {
      if (name.trim().length === 0) {
        setToast({ message: t('Vul eerst een naam in voor je poule.'), tone: 'error' })
        return
      }
      if (name.trim().length < 6) {
        setToast({ message: t('Geef je poule een naam van minimaal 6 tekens.'), tone: 'error' })
        return
      }
    }

    if (step < 3) setStep((value) => (value + 1) as Step)
    else submit()
  }

  function submit() {
    if (!scope) return

    createMutation.mutate({
      name,
      description,
      visibility,
      requiresApproval,
      audienceType,
      predictionReleaseMode: scope.type === 'tournament' ? releaseMode : 'all_open',
      pouleType: scope.type,
      scopeType: scope.type === 'club' ? TEAM_SCOPE_TYPE : LEAGUE_SCOPE_TYPE,
      scopeId: scope.item.id,
      thumbnailImage,
    })
  }

  function chooseCompetition() {
    if (!selectedLeague) return
    setScope({ type: 'competition', item: selectedLeague })
    setScopePickerView('type')
  }

  function chooseClub(team: PouleCreateTeam) {
    if (!selectedLeague) return
    setScope({ type: 'club', item: team })
    setScopePickerView('type')
  }

  function openScopePicker() {
    const nextView = scope?.type === 'tournament' ? 'tournaments' : 'countries'
    setScope(null)
    setSelectedCountry(null)
    setSelectedLeague(null)
    setScopePickerView(nextView)
  }

  const stepTitles = [t('Zelf een poule maken'), t('Bepaal voor wie de poule is'), t('Geef je poule een gezicht'), t('Stel de regels in')]

  if (createdPoule) {
    return (
      <PouleCreateSheet title={t('Poule aangemaakt')} isClosing={isClosing} onClose={requestClose}>
        <main className="mx-auto w-full max-w-2xl">
          <section className="rounded-[1.75rem] border border-white/[0.08] bg-[#0d1113] p-6 text-center shadow-[0_20px_60px_rgba(0,0,0,0.25)] sm:p-10">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-400/15 text-3xl text-emerald-300" aria-hidden="true">
            ✓
          </div>
          <p className="mt-6 text-xs font-bold uppercase tracking-[0.18em] text-accent-300">{t('Poule aangemaakt')}</p>
          <h1 className="mt-2 text-3xl font-black tracking-tight text-white">{createdPoule.name}</h1>
          <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-surface-400">
            {t('Je poule is klaar. Nodig deelnemers uit en begin met voorspellen.')}
          </p>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <Link
              to={`/poules/${createdPoule.id}`}
              className="inline-flex min-h-12 items-center justify-center rounded-xl bg-accent-600 px-5 py-3 text-sm font-bold text-white transition hover:bg-accent-500"
            >
              {t('Ga naar je poule')}
            </Link>
            {createdPoule.audienceType === 'company' ? (
              <Link
                to={'/poules/' + createdPoule.id + '/licentie'}
                className="inline-flex min-h-12 items-center justify-center rounded-xl border border-white/[0.12] px-5 py-3 text-sm font-bold text-surface-100 transition hover:bg-white/[0.07]"
              >
                {t('Upgrade nu')}
              </Link>
            ) : null}
          </div>
          </section>
        </main>
      </PouleCreateSheet>
    )
  }

  const scopeLabel = scope ? getScopeLabel(scope, t) : ''
  const isBusy = createMutation.isPending
  const sheetSubtitle = step === 0
    ? scopePickerView === 'type'
      ? t('Wil je een poule aanmaken voor een toernooi of club?')
      : undefined
    : undefined

  const scopeStepProps = {
    scope,
    pickerView: scopePickerView,
    selectedCountry,
    selectedLeague,
    countries: countriesQuery.data ?? [],
    leagues: leaguesQuery.data ?? [],
    teams: teamsQuery.data ?? [],
    tournaments: tournamentsQuery.data ?? [],
    countriesLoading: countriesQuery.isLoading,
    countriesError: getQueryError(countriesQuery.error, t),
    leaguesLoading: leaguesQuery.isLoading,
    leaguesError: getQueryError(leaguesQuery.error, t),
    teamsLoading: teamsQuery.isLoading,
    teamsError: getQueryError(teamsQuery.error, t),
    tournamentsLoading: tournamentsQuery.isLoading,
    tournamentsError: getQueryError(tournamentsQuery.error, t),
    onCountry: selectCountry,
    onLeague: selectLeague,
    onTournament: selectTournament,
    onCompetition: chooseCompetition,
    onClub: chooseClub,
    onOpenScopePicker: openScopePicker,
    onBackToCountries: backToCountries,
    onBackToLeagues: backToLeagues,
    onPickerView: setScopePickerView,
    t,
  }

  return (
    <PouleCreateSheet
      title={stepTitles[step] || ''}
      subtitle={sheetSubtitle}
      isClosing={isClosing}
      onClose={requestClose}
      contentRef={contentRef}
      toast={toast}
    >
      <main className="mx-auto flex min-h-full w-full max-w-3xl flex-col">
        <div className="sticky top-[-1.25rem] z-20 -mx-5 mb-6 grid grid-cols-4 gap-2 border-b border-white/[0.07] bg-[#0d1113]/[0.97] px-5 pb-4 pt-1 shadow-[0_12px_18px_rgba(13,17,19,0.8)] backdrop-blur sm:-mx-6 sm:px-6" aria-label={t('Voortgang poule aanmaken')}>
          {stepTitles.map((title, index) => (
            <div key={title}>
              <div className={['h-1.5 rounded-full', index <= step ? 'bg-accent-500' : 'bg-white/[0.1]'].join(' ')} />
              <span className="sr-only">{index + 1}. {title}</span>
            </div>
          ))}
        </div>

      {step === 0 ? (
        <ScopeStep {...scopeStepProps} showPickerView={false} />
      ) : null}

      {step === 0 && scopePickerView !== 'type' ? (
        <PouleScopePickerSheet title={undefined} onClose={resetScope}>
          <ScopeStep {...scopeStepProps} />
        </PouleScopePickerSheet>
      ) : null}

      {step === 1 ? (
        <AudienceStep audienceType={audienceType} onChange={setAudienceType} scopeLabel={scopeLabel} t={t} />
      ) : null}

      {step === 2 ? (
        <DetailsStep
          name={name}
          description={description}
          thumbnailPreview={thumbnailPreview}
          onName={setName}
          onDescription={setDescription}
          onImage={setThumbnailImage}
          scopeLabel={scopeLabel}
          audienceLabel={audienceType === 'company' ? t('Bedrijf of vereniging') : t('Vrienden')}
          t={t}
        />
      ) : null}

      {step === 3 ? (
        <SettingsStep
          scope={scope}
          visibility={visibility}
          requiresApproval={requiresApproval}
          releaseMode={releaseMode}
          onVisibility={setVisibility}
          onRequiresApproval={setRequiresApproval}
          onReleaseMode={setReleaseMode}
          t={t}
        />
      ) : null}

        {createMutation.isError ? (
          <div role="alert" className="mt-4 rounded-xl border border-red-900/60 bg-red-950/30 p-4 text-sm text-red-200">
            {createMutation.error instanceof ApiError && createMutation.error.message
              ? createMutation.error.message
              : t('De poule kon niet worden aangemaakt. Controleer je gegevens en probeer het opnieuw.')}
          </div>
        ) : null}

        <footer className="sticky bottom-0 z-20 -mx-5 mt-auto flex flex-row items-center justify-between gap-3 border-t border-white/[0.07] bg-[#0d1113]/[0.97] px-5 pb-4 pt-4 shadow-[0_-14px_24px_rgba(13,17,19,0.9)] backdrop-blur sm:-mx-6 sm:px-6">
          <button
            type="button"
            onClick={() => setStep((value) => (value > 0 ? (value - 1) as Step : value))}
            disabled={step === 0 || isBusy}
            className="min-h-12 flex-1 rounded-xl border border-white/[0.12] bg-[#151a1c] px-4 py-3 text-sm font-bold text-white transition hover:bg-white/[0.09] disabled:cursor-not-allowed disabled:opacity-40 sm:flex-none sm:px-5"
          >
            {t('Terug')}
          </button>
          <button
            type="button"
            onClick={goNext}
            disabled={isBusy}
            className={[
              'min-h-12 flex-1 rounded-xl px-4 py-3 text-sm font-bold transition disabled:cursor-wait disabled:opacity-60 sm:flex-none sm:px-6',
              step === 3
                ? 'bg-accent-600 text-white hover:bg-accent-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-300 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0d1113]'
                : 'bg-accent-600 text-white hover:bg-accent-500',
            ].join(' ')}
          >
            {isBusy ? t('Poule wordt aangemaakt...') : step === 3 ? t('Afronden') : t('Volgende')}
          </button>
        </footer>
      </main>
    </PouleCreateSheet>
  )
}

function PouleCreateSheet({ title, subtitle, isClosing, onClose, contentRef, toast, children }: { title: string; subtitle?: string | undefined; isClosing: boolean; onClose: () => void; contentRef?: RefObject<HTMLDivElement | null>; toast?: ToastState | null; children: ReactNode }) {
  const { t } = useTranslation()

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        onClick={onClose}
        aria-label={t('Poule aanmaken sluiten')}
        className={`${isClosing ? 'animate-drawer-backdrop-out' : 'animate-drawer-backdrop-in'} absolute inset-0 bg-black/80 backdrop-blur-sm`}
      />
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="poule-create-sheet-title"
        className={`${isClosing ? 'animate-manager-player-picker-out' : 'animate-manager-player-picker-in'} absolute inset-x-0 bottom-0 mx-auto flex h-[min(92dvh,760px)] max-h-[92dvh] w-full max-w-4xl flex-col overflow-hidden rounded-t-[1.5rem] border-t border-white/[0.1] bg-[#0d1113] shadow-[0_-20px_64px_rgba(0,0,0,0.5)]`}
      >
        <header className="flex shrink-0 items-start justify-between gap-4 border-b border-white/[0.07] p-5 sm:p-6">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-accent-200">{t('Poule aanmaken')}</p>
            <h1 id="poule-create-sheet-title" className="mt-1 text-xl font-black tracking-tight text-white sm:text-2xl">{title}</h1>
            {subtitle ? <p className="mt-2 max-w-2xl text-sm leading-6 text-surface-400">{subtitle}</p> : null}
          </div>
          <ModalCloseButton onClick={onClose} label={t('Poule aanmaken sluiten')} className="h-9 w-9" />
        </header>
        <div ref={contentRef} className="min-h-0 flex-1 overflow-y-auto px-5 pb-0 pt-5 sm:px-6 sm:pb-0 sm:pt-6">{children}</div>
      </section>
      {toast ? <PouleCreateToast message={toast.message} /> : null}
    </div>
  )
}

function PouleCreateToast({ message }: { message: string }) {
  return (
    <div role="alert" aria-live="assertive" className="pointer-events-none fixed inset-x-0 bottom-5 z-[70] flex justify-center px-4 sm:bottom-7">
      <div className="flex items-center gap-2 rounded-full border border-red-300/25 bg-[#0d1113] px-4 py-2.5 text-sm font-bold text-red-100 shadow-[0_16px_40px_rgba(0,0,0,0.38)]">
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-300/15 text-xs text-red-300" aria-hidden="true">!</span>
        {message}
      </div>
    </div>
  )
}

function PouleScopePickerSheet({ title, onClose, children }: { title: string | undefined; onClose: () => void; children: ReactNode }) {
  const { t } = useTranslation()

  return (
    <div className="fixed inset-0 z-[60]">
      <button type="button" onClick={onClose} aria-label={t('Poule kiezen sluiten')} className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <section role="dialog" aria-modal="true" aria-labelledby="poule-scope-picker-title" className="animate-manager-player-picker-in absolute inset-x-0 bottom-0 mx-auto flex h-[min(78dvh,640px)] max-h-[78dvh] w-full max-w-3xl flex-col overflow-hidden rounded-t-[1.5rem] border-t border-white/[0.1] bg-[#0d1113] shadow-[0_-20px_64px_rgba(0,0,0,0.5)]">
        <header className="flex shrink-0 items-center justify-between gap-4 border-b border-white/[0.07] px-5 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <span className="h-1 w-8 rounded-full bg-white/20" aria-hidden="true" />
            <h2 id="poule-scope-picker-title" className="text-base font-black text-white">{title ?? t('Poule maken')}</h2>
          </div>
          <ModalCloseButton onClick={onClose} label={t('Poule kiezen sluiten')} className="h-9 w-9" />
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto p-5 sm:p-6">{children}</div>
      </section>
    </div>
  )
}

function ScopeStep({
  scope,
  pickerView,
  selectedCountry,
  selectedLeague,
  countries,
  leagues,
  teams,
  tournaments,
  countriesLoading,
  countriesError,
  leaguesLoading,
  leaguesError,
  teamsLoading,
  teamsError,
  tournamentsLoading,
  tournamentsError,
  onCountry,
  onLeague,
  onTournament,
  onCompetition,
  onClub,
  onOpenScopePicker,
  onBackToCountries,
  onBackToLeagues,
  onPickerView,
  showPickerView = true,
  t,
}: {
  scope: ScopeSelection | null
  pickerView: ScopePickerView
  selectedCountry: PouleCreateCountry | null
  selectedLeague: PouleCreateLeague | null
  countries: PouleCreateCountry[]
  leagues: PouleCreateLeague[]
  teams: PouleCreateTeam[]
  tournaments: PouleCreateTournament[]
  countriesLoading: boolean
  countriesError: string
  leaguesLoading: boolean
  leaguesError: string
  teamsLoading: boolean
  teamsError: string
  tournamentsLoading: boolean
  tournamentsError: string
  onCountry: (country: PouleCreateCountry) => void
  onLeague: (league: PouleCreateLeague) => void
  onTournament: (tournament: PouleCreateTournament) => void
  onCompetition: () => void
  onClub: (team: PouleCreateTeam) => void
  onOpenScopePicker: () => void
  onBackToCountries: () => void
  onBackToLeagues: () => void
  onPickerView: (view: ScopePickerView) => void
  showPickerView?: boolean
  t: (key: string, values?: Record<string, string | number>) => string
}) {
  const [showClubPicker, setShowClubPicker] = useState(false)

  useEffect(() => {
    setShowClubPicker(false)
  }, [selectedLeague?.id])

  if (scope) {
    const image = getScopeImage(scope)
    return (
      <div className="space-y-4">
        <div className="rounded-[1.5rem] border border-accent-400/30 bg-accent-500/10 p-5">
          <div className="flex items-center gap-4">
            <PickerImage src={image} alt="" fallback="⚽" />
            <div className="min-w-0 flex-1">
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-accent-200">{t('Jouw keuze')}</p>
              <h2 className="mt-1 truncate text-xl font-black text-white">{scope.item.name}</h2>
              <p className="mt-1 text-sm text-surface-300">{getScopeLabel(scope, t)}</p>
            </div>
          </div>
          <button type="button" onClick={onOpenScopePicker} className="mt-5 inline-flex items-center gap-2 rounded-lg border border-white/[0.1] px-3 py-2 text-xs font-bold text-surface-300 transition hover:border-accent-400/40 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-300">
            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="m14.5 5.5 4 4M5 19l3.8-.8L19.7 7.3a2.1 2.1 0 0 0-3-3L5.8 15.2 5 19Z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {t('Bewerken')}
          </button>
        </div>
      </div>
    )
  }

  if (!showPickerView) {
    return (
      <div className="space-y-5">
        <div className="grid gap-3 sm:grid-cols-2">
          <ScopeTypeCard title={t('Toernooipoule')} body={t('Voorspel een volledig toernooi')} icon={<PouleTypeIcon type="tournament" />} onClick={() => onPickerView('tournaments')} />
          <ScopeTypeCard title={t('Competitie of club')} body={t('Kies eerst een land en daarna een competitie')} icon={<PouleTypeIcon type="competition" />} onClick={() => onPickerView('countries')} />
        </div>
      </div>
    )
  }

  if (pickerView === 'league-detail' && selectedLeague) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3 rounded-xl border border-white/[0.08] bg-[#111719] p-3">
          <PickerImage src={selectedLeague.image_path} alt="" fallback="🏆" small />
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-surface-500">{t('Gekozen competitie')}</p>
            <p className="truncate text-sm font-black text-white">{selectedLeague.name}</p>
          </div>
        </div>
        <p className="text-sm leading-6 text-surface-300">{t('Wil je een poule van de hele competitie of alleen een club?')}</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <ScopeTypeCard title={t('Hele competitie')} body={t('Maak een poule voor alle wedstrijden')} icon={<PouleTypeIcon type="competition" />} onClick={onCompetition} />
          <ScopeTypeCard selected={showClubPicker} title={t('Alleen een club')} body={t('Kies één club uit deze competitie')} icon={<PouleTypeIcon type="club" />} onClick={() => setShowClubPicker(true)} />
        </div>
        {showClubPicker ? <>
          {teamsLoading ? <LoadingLine label={t('Clubs laden...')} /> : null}
          {teamsError ? <ErrorBox message={teamsError} /> : null}
          {!teamsLoading && teams.length > 0 ? (
            <div className="grid gap-2 sm:grid-cols-2">
              {teams.map((team) => (
                <button key={team.id} type="button" onClick={() => onClub(team)} className="flex items-center gap-3 rounded-xl border border-white/[0.08] bg-[#111719] p-3 text-left transition hover:border-accent-400/40 hover:bg-[#171f21]">
                  <PickerImage src={team.logo_url} alt="" fallback="⚽" small />
                  <span className="truncate text-sm font-bold text-white">{team.name}</span>
                </button>
              ))}
            </div>
          ) : null}
          {!teamsLoading && teams.length === 0 && !teamsError ? <EmptyBox message={t('Geen clubs gevonden voor deze competitie.')} /> : null}
        </> : null}
        <button type="button" onClick={onBackToLeagues} className="text-sm font-bold text-surface-400 transition hover:text-white">← {t('Competitie wijzigen')}</button>
      </div>
    )
  }

  if (pickerView === 'leagues' && selectedCountry) {
    return (
      <div className="space-y-4">
        <button type="button" onClick={onBackToCountries} className="text-sm font-bold text-surface-400 transition hover:text-white">← {t('Land wijzigen')}</button>
        {leaguesLoading ? <LoadingLine label={t('Competities laden...')} /> : null}
        {leaguesError ? <ErrorBox message={leaguesError} /> : null}
        {!leaguesLoading && leagues.length > 0 ? (
          <div className="grid gap-3 sm:grid-cols-2">
            {leagues.map((league) => (
              <PickerCard key={league.id} title={league.name} image={league.image_path} fallback="🏆" onClick={() => onLeague(league)} />
            ))}
          </div>
        ) : null}
        {!leaguesLoading && leagues.length === 0 && !leaguesError ? <EmptyBox message={t('Geen competities gevonden voor dit land.')} /> : null}
      </div>
    )
  }

  if (pickerView === 'tournaments') {
    return (
      <div className="space-y-4">
        <StepIntro title={t('Kies een toernooi')} body={t('Selecteer het toernooi waarvoor je een poule wilt maken.')} t={t} />
        {tournamentsLoading ? <LoadingLine label={t('Toernooien laden...')} /> : null}
        {tournamentsError ? <ErrorBox message={tournamentsError} /> : null}
        {!tournamentsLoading && tournaments.length > 0 ? (
          <div className="grid gap-3 sm:grid-cols-2">
            {tournaments.map((tournament) => (
              <PickerCard key={tournament.id} title={tournament.name} image={tournament.image_path} fallback="🏆" onClick={() => onTournament(tournament)} subtitle={tournament.seasons?.[0]?.name} />
            ))}
          </div>
        ) : null}
        {!tournamentsLoading && tournaments.length === 0 && !tournamentsError ? <EmptyBox message={t('Geen toernooien gevonden.')} /> : null}
        <button type="button" onClick={() => onPickerView('type')} className="text-sm font-bold text-surface-400 transition hover:text-white">← {t('Wijzigen')}</button>
      </div>
    )
  }

  if (pickerView === 'countries') {
    return (
      <div className="space-y-4">
        {countriesLoading ? <LoadingLine label={t('Landen laden...')} /> : null}
        {countriesError ? <ErrorBox message={countriesError} /> : null}
        {!countriesLoading && countries.length > 0 ? (
          <div className="grid gap-2 sm:grid-cols-2">
            {countries.map((country) => (
              <button key={country.id} type="button" onClick={() => onCountry(country)} className="flex items-center gap-3 rounded-xl border border-white/[0.08] bg-[#111719] p-3 text-left transition hover:border-accent-400/40 hover:bg-[#171f21]">
                <PickerImage src={country.image_path ?? country.flag_url} alt="" fallback="🌍" small />
                <span className="truncate text-sm font-bold text-white">{country.name}</span>
              </button>
            ))}
          </div>
        ) : null}
        {!countriesLoading && countries.length === 0 && !countriesError ? <EmptyBox message={t('Geen landen gevonden.')} /> : null}
        <button type="button" onClick={() => onPickerView('type')} className="text-sm font-bold text-surface-400 transition hover:text-white">← {t('Wijzigen')}</button>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-2">
        <ChoiceCard title={t('Toernooipoule')} body={t('Voorspel een volledig toernooi')} onClick={() => onPickerView('tournaments')} />
        <ChoiceCard title={t('Competitie of club')} body={t('Kies eerst een land en daarna een competitie')} onClick={() => onPickerView('countries')} />
      </div>
    </div>
  )
}

function AudienceStep({ audienceType, onChange, scopeLabel, t }: { audienceType: AudienceType; onChange: (value: AudienceType) => void; scopeLabel: string; t: (key: string) => string }) {
  return (
    <div className="space-y-5">
      <StepIntro title={t('Voor wie maak je deze poule?')} body={scopeLabel ? `${t('Poule gebaseerd op')}: ${scopeLabel}` : t('Kies wie er mee kan doen met deze poule.')} t={t} />
      <div className="grid gap-3 sm:grid-cols-2">
        <SelectableCard selected={audienceType === 'friends'} title={t('Vrienden')} body={t('Nieuwe gratis poules hebben maximaal 15 deelnemers, inclusief de eigenaar.')} icon="👥" onClick={() => onChange('friends')} />
        <SelectableCard selected={audienceType === 'company'} title={t('Bedrijf of vereniging')} body={t('Een poule voor je bedrijf, vereniging of organisatie.')} icon="🏢" onClick={() => onChange('company')} />
      </div>
    </div>
  )
}

function DetailsStep({ name, description, thumbnailPreview, onName, onDescription, onImage, scopeLabel, audienceLabel, t }: { name: string; description: string; thumbnailPreview: string | null; onName: (value: string) => void; onDescription: (value: string) => void; onImage: (file: File | undefined) => void; scopeLabel: string; audienceLabel: string; t: (key: string) => string }) {
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-2">
        <span className="rounded-full border border-white/[0.08] bg-[#111719] px-3 py-2 text-xs font-semibold text-surface-300">⚽ {scopeLabel}</span>
        <span className="rounded-full border border-white/[0.08] bg-[#111719] px-3 py-2 text-xs font-semibold text-surface-300">👥 {audienceLabel}</span>
      </div>
      <div className="space-y-4 rounded-[1.5rem] border border-white/[0.08] bg-[#0d1113] p-5 sm:p-6">
        <label className="block text-sm font-bold text-white">
          {t('Naam van de poule')}
          <input value={name} onChange={(event) => onName(event.target.value)} maxLength={255} autoFocus className="mt-2 min-h-12 w-full rounded-xl border border-white/[0.12] bg-[#111719] px-4 text-sm font-semibold text-white outline-none transition placeholder:text-surface-500 focus:border-accent-400/70" placeholder={t('Bijvoorbeeld: Familie WK 2026')} />
          <span className="mt-1 block text-xs font-normal text-surface-500">{t('Minimaal 6 tekens')}</span>
        </label>
        <label className="block text-sm font-bold text-white">
          {t('Beschrijving')} <span className="font-normal text-surface-500">({t('optioneel')})</span>
          <textarea value={description} onChange={(event) => onDescription(event.target.value)} rows={4} className="mt-2 w-full resize-y rounded-xl border border-white/[0.12] bg-[#111719] px-4 py-3 text-sm font-semibold text-white outline-none transition placeholder:text-surface-500 focus:border-accent-400/70" placeholder={t('Waar gaat deze poule over?')} />
        </label>
        <label className="flex cursor-pointer items-center gap-4 rounded-xl border border-dashed border-white/[0.16] bg-[#111719] p-4 transition hover:border-accent-400/50">
          {thumbnailPreview ? <img src={thumbnailPreview} alt="" className="h-16 w-16 rounded-xl object-cover" /> : <ImagePlaceholderIcon />}
          <span className="min-w-0 flex-1"><span className="block text-sm font-bold text-white">{t('Poule-afbeelding')}</span><span className="mt-1 block text-xs text-surface-400">{t('Voeg optioneel een afbeelding toe (maximaal 2 MB).')}</span></span>
          <input type="file" accept="image/jpeg,image/png,image/webp" className="sr-only" onChange={(event) => onImage(event.target.files?.[0])} />
        </label>
      </div>
    </div>
  )
}

function SettingsStep({ scope, visibility, requiresApproval, releaseMode, onVisibility, onRequiresApproval, onReleaseMode, t }: { scope: ScopeSelection | null; visibility: 'public' | 'private'; requiresApproval: boolean; releaseMode: ReleaseMode; onVisibility: (value: 'public' | 'private') => void; onRequiresApproval: (value: boolean) => void; onReleaseMode: (value: ReleaseMode) => void; t: (key: string) => string }) {
  return (
    <div className="space-y-5">
      {scope?.type === 'tournament' ? (
        <div className="rounded-[1.5rem] border border-white/[0.08] bg-[#0d1113] p-5 sm:p-6">
          <h2 className="text-base font-black text-white">{t('Hoe wil je dat deelnemers voorspellen?')}</h2>
          <p className="mt-1 text-sm leading-6 text-surface-400">{t('Kies of deelnemers wedstrijden afzonderlijk voorspellen of de hele fase vooraf invullen.')}</p>
          <div className="mt-4 space-y-2">
            <SelectableCard selected={releaseMode === 'all_open'} title={t('Per wedstrijd voorspellen')} badge={t('Meest gekozen')} body={t('Voorspel wedstrijden afzonderlijk tot de aftrap.')} icon={<SettingsIcon type="unlock" />} onClick={() => onReleaseMode('all_open')} compact />
            <SelectableCard selected={releaseMode === 'stage_by_stage'} title={t('Traditioneel')} body={t('Voorspel de hele fase vóór de eerste wedstrijd begint. Daarna wordt de fase vergrendeld.')} example={t('Bijvoorbeeld: de poulefase')} icon={<SettingsIcon type="lock" />} onClick={() => onReleaseMode('stage_by_stage')} compact />
          </div>
        </div>
      ) : null}
      <div className="rounded-[1.5rem] border border-white/[0.08] bg-[#0d1113] p-5 sm:p-6">
        <h2 className="text-base font-black text-white">{t('Zichtbaarheid van de poule')}</h2>
        <div className="mt-4 space-y-2">
          <SelectableCard selected={visibility === 'public'} title={t('Openbaar')} body={t('Iedereen kan de poule vinden en meedoen.')} icon={<SettingsIcon type="globe" />} onClick={() => onVisibility('public')} compact />
          <SelectableCard selected={visibility === 'private'} title={t('Privé')} body={t('Alleen mensen met de uitnodiging kunnen de poule vinden.')} icon={<SettingsIcon type="private" />} onClick={() => onVisibility('private')} compact />
        </div>
      </div>
      <div className="rounded-[1.5rem] border border-white/[0.08] bg-[#0d1113] p-5 sm:p-6">
        <h2 className="text-base font-black text-white">{t('Toegang tot de poule')}</h2>
        <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-xl border border-white/[0.08] bg-[#111719] p-4">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center text-surface-300" aria-hidden="true"><SettingsIcon type="approval" /></span>
          <span className="min-w-0 flex-1"><span className="block text-sm font-bold text-white">{t('Deelname eerst goedkeuren')}</span><span className="mt-1 block text-xs leading-5 text-surface-400">{t('Je moet nieuwe deelnemers eerst goedkeuren voordat ze kunnen meedoen.')}</span></span>
          <span className={['mt-1 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2', requiresApproval ? 'border-accent-300 bg-accent-400 text-[#0d1113]' : 'border-surface-600'].join(' ')} aria-hidden="true">
            {requiresApproval ? <svg className="h-2.5 w-2.5" viewBox="0 0 12 12" fill="none"><path d="m2.5 6 2.2 2.2L9.5 3.8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg> : null}
          </span>
          <input type="checkbox" checked={requiresApproval} onChange={(event) => onRequiresApproval(event.target.checked)} className="sr-only" />
        </label>
      </div>
    </div>
  )
}

function StepIntro({ title, body }: { title: string; body?: string; t: (key: string) => string }) {
  return <div className="rounded-[1.5rem] border border-white/[0.08] bg-[#0d1113] p-5 sm:p-6"><h2 className="text-xl font-black tracking-tight text-white">{title}</h2>{body ? <p className="mt-2 max-w-xl text-sm leading-6 text-surface-400">{body}</p> : null}</div>
}

function ChoiceCard({ title, body, onClick }: { title: string; body: string; onClick: () => void }) {
  return <button type="button" onClick={onClick} className="flex items-center gap-3 rounded-xl border border-white/[0.08] bg-[#111719] px-4 py-3 text-left transition hover:border-accent-400/50 hover:bg-[#171f21] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-300"><span className="min-w-0 flex-1"><span className="block text-sm font-black text-white">{title}</span><span className="mt-0.5 block truncate text-xs leading-5 text-surface-400">{body}</span></span><span className="shrink-0 text-xl leading-none text-surface-500" aria-hidden="true">›</span></button>
}

function ScopeTypeCard({ title, body, icon, selected = false, onClick }: { title: string; body: string; icon: ReactNode; selected?: boolean; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className={['flex items-center gap-3 rounded-xl border p-4 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-300', selected ? 'border-accent-400/60 bg-accent-500/10' : 'border-white/[0.08] bg-[#111719] hover:border-accent-400/50 hover:bg-[#171f21]'].join(' ')}>
      <span className="flex h-7 w-7 shrink-0 items-center justify-center text-accent-300" aria-hidden="true">{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-black text-white">{title}</span>
        <span className="mt-1 block truncate text-xs leading-5 text-surface-400">{body}</span>
      </span>
      <span className={['flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2', selected ? 'border-accent-300 bg-accent-400' : 'border-surface-600'].join(' ')} aria-hidden="true">
        {selected ? <span className="h-1.5 w-1.5 rounded-full bg-[#0d1113]" /> : null}
      </span>
    </button>
  )
}

function PouleTypeIcon({ type }: { type: 'tournament' | 'competition' | 'club' }) {
  return type === 'tournament' ? (
    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none">
      <path d="M7 4h10v3.5A5 5 0 0 1 12 12a5 5 0 0 1-5-4.5V4Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M7 6H4.5v1a3.5 3.5 0 0 0 3.2 3.5M17 6h2.5v1a3.5 3.5 0 0 1-3.2 3.5M12 12v4M8.5 20h7M10 16h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ) : type === 'competition' ? (
    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M3.8 12h16.4M12 3.5c2.1 2.3 3.2 5.1 3.2 8.5S14.1 18.2 12 20.5c-2.1-2.3-3.2-5.1-3.2-8.5S9.9 5.8 12 3.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ) : (
    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none">
      <path d="M12 3.5 19 6v5.2c0 4.2-2.8 7.7-7 9.3-4.2-1.6-7-5.1-7-9.3V6l7-2.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M8.5 12.5h7M10 10h4M10 15h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

function SelectableCard({ selected, title, badge, body, example, icon, onClick, compact = false }: { selected: boolean; title: string; badge?: string; body: string; example?: string; icon: ReactNode; onClick: () => void; compact?: boolean }) {
  return <button type="button" onClick={onClick} className={['flex w-full items-start gap-3 rounded-xl border p-4 text-left transition', selected ? 'border-accent-400/60 bg-accent-500/10' : 'border-white/[0.08] bg-[#111719] hover:border-accent-400/40 hover:bg-[#171f21]', compact ? 'py-3' : ''].join(' ')}><span className="flex h-6 w-6 shrink-0 items-center justify-center text-surface-300" aria-hidden="true">{icon}</span><span className="min-w-0 flex-1"><span className="flex flex-wrap items-center gap-2"><span className="text-sm font-black text-white">{title}</span>{badge ? <span className="rounded-full border border-accent-300/30 bg-accent-400/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em] text-accent-200">{badge}</span> : null}</span><span className="mt-1 block text-xs leading-5 text-surface-400">{body}</span>{example ? <span className="mt-1 block text-xs font-semibold text-surface-500">{example}</span> : null}</span><span className={['mt-1 h-4 w-4 shrink-0 rounded-full border-2', selected ? 'border-accent-300 bg-accent-400' : 'border-surface-600'].join(' ')} aria-hidden="true" /></button>
}

function SettingsIcon({ type }: { type: 'unlock' | 'lock' | 'globe' | 'private' | 'approval' }) {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none">
      {type === 'globe' ? <>
        <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.5" />
        <path d="M3.8 12h16.4M12 3.5c2.1 2.3 3.2 5.1 3.2 8.5S14.1 18.2 12 20.5c-2.1-2.3-3.2-5.1-3.2-8.5S9.9 5.8 12 3.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </> : type === 'approval' ? <>
        <circle cx="9" cy="8" r="3" stroke="currentColor" strokeWidth="1.5" />
        <path d="M3.8 18.5c.8-2.7 2.5-4 5.2-4 1 0 1.9.2 2.6.6M14 17l1.5 1.5 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </> : type === 'private' ? <>
        <rect x="5" y="10" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.5" />
        <path d="M8 10V8a4 4 0 0 1 8 0v2M12 14v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </> : type === 'unlock' ? <>
        <rect x="5" y="10" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.5" />
        <path d="M8 10V8a4 4 0 0 1 7.1-2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M12 14v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </> : <>
        <rect x="5" y="10" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.5" />
        <path d="M8 10V8a4 4 0 0 1 8 0v2M12 14v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </>}
    </svg>
  )
}

function PickerCard({ title, subtitle, image, fallback, onClick }: { title: string; subtitle?: string | undefined; image?: string | null | undefined; fallback: string; onClick: () => void }) {
  return <button type="button" onClick={onClick} className="flex items-center gap-3 rounded-xl border border-white/[0.08] bg-[#111719] p-3 text-left transition hover:border-accent-400/40 hover:bg-[#171f21]"><PickerImage src={image} alt="" fallback={fallback} small /><span className="min-w-0"><span className="block truncate text-sm font-bold text-white">{title}</span>{subtitle ? <span className="mt-1 block truncate text-xs text-surface-400">{subtitle}</span> : null}</span></button>
}

function PickerImage({ src, alt, fallback, small = false }: { src?: string | null | undefined; alt: string; fallback: string; small?: boolean }) {
  const [failed, setFailed] = useState(false)
  const size = small ? 'h-10 w-10 text-lg' : 'h-14 w-14 text-2xl'
  if (!src || failed) return <span className={`flex shrink-0 items-center justify-center rounded-xl bg-white/[0.06] ${size}`} aria-hidden="true">{fallback}</span>
  return <img src={src} alt={alt} onError={() => setFailed(true)} className={`shrink-0 rounded-xl bg-white/[0.06] object-contain p-1 ${size}`} />
}

function LoadingLine({ label }: { label: string }) {
  return <div role="status" className="rounded-xl border border-white/[0.08] bg-[#111719] p-4 text-sm text-surface-400">{label}</div>
}

function ErrorBox({ message }: { message: string }) {
  return <div role="alert" className="rounded-xl border border-red-900/60 bg-red-950/30 p-4 text-sm text-red-200">{message}</div>
}

function EmptyBox({ message }: { message: string }) {
  return <div className="rounded-xl border border-white/[0.08] bg-[#111719] p-4 text-sm text-surface-400">{message}</div>
}

function ImagePlaceholderIcon() {
  return (
    <span className="flex h-16 w-16 items-center justify-center rounded-xl bg-white/[0.06] text-surface-500" aria-hidden="true">
      <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none">
        <rect x="3.5" y="4" width="17" height="16" rx="2.5" stroke="currentColor" strokeWidth="1.6" />
        <circle cx="8.5" cy="9" r="1.5" stroke="currentColor" strokeWidth="1.6" />
        <path d="m5.5 17 4.2-4.2 2.8 2.8 2-2 4 3.4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  )
}

function getScopeLabel(scope: ScopeSelection, t: (key: string) => string) {
  if (scope.type === 'club') return t('Clubpoule')
  if (scope.type === 'competition') return t('Competitiepoule')
  return scope.item.name
}

function getScopeImage(scope: ScopeSelection) {
  return scope.type === 'club' ? scope.item.logo_url : scope.item.image_path
}

function getQueryError(...args: unknown[]) {
  const translate = args.pop() as (key: string) => string
  const error = args.find((value) => value instanceof Error) as Error | undefined
  return error ? translate('De keuzelijsten konden niet worden geladen.') : ''
}
